// Tiny fuzzy matcher for the command palette.
//
// Returns a score (higher is better) or -1 when `query` is not a
// subsequence of `text`. Contiguous substring matches beat scattered ones,
// matches at word starts beat mid-word ones, and shorter texts win ties.

export function fuzzyScore(query: string, text: string): number {
  const q = query.trim().toLowerCase();
  if (!q) return 0;
  const t = text.toLowerCase();

  const sub = t.indexOf(q);
  if (sub >= 0) {
    const wordStart = sub === 0 || /[\s\-_./:]/.test(t[sub - 1]);
    return 1000 + (wordStart ? 200 : 0) - sub - t.length * 0.1;
  }

  // Subsequence match with bonuses for runs and word starts.
  let score = 0;
  let ti = 0;
  let prev = -2;
  for (const ch of q) {
    if (ch === ' ') continue;
    const found = t.indexOf(ch, ti);
    if (found < 0) return -1;
    if (found === prev + 1) score += 8;
    if (found === 0 || /[\s\-_./:]/.test(t[found - 1])) score += 5;
    score -= Math.min(found - ti, 10) * 0.5;
    prev = found;
    ti = found + 1;
  }
  return score - t.length * 0.1;
}

/** Filter + sort `items` by fuzzy score of `getText(item)`; keeps at most
 *  `limit`. With an empty query returns the first `limit` items unchanged. */
export function fuzzyFilter<T>(
  items: T[],
  query: string,
  getText: (item: T) => string,
  limit = Infinity,
): T[] {
  if (!query.trim()) return items.slice(0, limit);
  const scored: { item: T; score: number }[] = [];
  for (const item of items) {
    const s = fuzzyScore(query, getText(item));
    if (s >= 0) scored.push({ item, score: s });
  }
  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((x) => x.item);
}
